<!--
                               o8o                 .
                               `"'               .o8
   .oooo.o  .ooooo.  oooo d8b oooo  oo.ooooo.  .o888oo
  d88(  "8 d88' `"Y8 `888""8P `888   888' `88b   888
  `"Y88b.  888        888      888   888   888   888
  o.  )88b 888   .o8  888      888   888   888   888 .
  8""888P' `Y8bod8P' d888b    o888o  888bod8P'   "888"
                                     888
                                    o888o
 -->

<script>

import { TweenMax as Tween, TimelineMax as Line, Power1 } from 'gsap'

export default {

  name: 'SliderWidget',

  props: {
    content: [Object]
  },

  data () {
    return {
      tl: null,
      offset: 0,
      scroll: 0,
      maxScroll: 0
    }
  },

  watch: {
    '$store.state.app.window.width': 'getOffset',
  },

  mounted () {
    this.getOffset()
  },

  methods: {

    getOffset () {

      let images = this.$refs.img;

      for(let i=0; i < images.length; i++ ) {
        images[i].style.height = 'auto'
      }

      this.$nextTick(() => {
        for(let i=0; i < images.length; i++ ) {
          images[i].style.height = '100%'
        }
      });

      this.offset = parseInt(getComputedStyle(this.$refs.slides[0]).marginLeft);
      this.maxScroll = this.$refs.track.offsetWidth - this.$refs.scrollable.offsetWidth;

    },

    getScroll () {
      this.scroll = this.$refs.scrollable.scrollLeft
    },

    changeSlide (mod) {
      const closestSlide = this.$refs.slides.reduce((closest, slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - this.offset)

        if (distance < closest.distance) {
          closest.slide = slide
          closest.distance = distance
          closest.index = index
        }

        return closest
      }, { slide: null, distance: 9999999, index: -1 })

      const nextSlideIndex = mod < 0
        ? Math.max(closestSlide.index + mod, 0)
        : Math.min(closestSlide.index + mod, this.$refs.slides.length - 1)

      const scroll = this.$refs.slides[nextSlideIndex].offsetLeft - this.offset

      Tween.to(this.$refs.scrollable, 0.5, {
        scrollTo: {
          x: nextSlideIndex >= this.$refs.slides.length - 1 ? 'max' : scroll,
          autoKill: false
        },
        overwrite: 'all',
        ease: Power1.easeInOut
      })

      this.maxScroll = this.$refs.track.offsetWidth - this.$refs.scrollable.offsetWidth;
    },

    // ===========================================
    // Animations
    // ===========================================

    beforeAppear () {
      const tl = this.tl = new Line({ paused: true, onStart: this.getOffset })
      const group = []

      if (this.$refs.title) group.push(this.$refs.title)
      if (this.$refs.content) group.push(this.$refs.content)
      group.push(this.$refs.arrows)

      tl.from(this.$refs.bg, 1, {
        scaleY: 0,
        ease: Power1.easeOut
      })

      tl.staggerFrom(group, 1, {
        autoAlpha: 0,
        y: -20,
        ease: Power1.easeOut
      }, 0.1, 0.3)

      tl.staggerFrom(this.$refs.slides, 1, {
        autoAlpha: 0,
        ease: Power1.easeOut
      }, 0.2, 0.3)
    },

    appear () {
      this.tl.play()
    }
  }

}

</script>


<!--

  oooo                                                  .
  `888                                                .o8
   888   .oooo.   oooo    ooo  .ooooo.  oooo  oooo  .o888oo
   888  `P  )88b   `88.  .8'  d88' `88b `888  `888    888
   888   .oP"888    `88..8'   888   888  888   888    888
   888  d8(  888     `888'    888   888  888   888    888 .
  o888o `Y888""8o     .8'     `Y8bod8P'  `V88V"V8P'   "888"
                  .o..P'
                  `Y8P'
 -->


<template lang="pug">

.widget
  .widget__background(ref="bg")
  .widget__content
    .widget__text
      .widget-title(v-if="content.title" ref="title") {{ content.title }}
      .widget-description.markdown-container(v-if="content.description" v-md="content.description" ref="content")
    .widget__slider.slider
      .slider-arrows-container
        .slider-arrows(ref="arrows")
          button.slider-arrows__item.slider-arrow(type="button" @click="changeSlide(-1)", :disabled="scroll <= offset")
            img.slider-arrow__static(src="../../../assets/arrow-prev-blue.svg")
            img.slider-arrow__hover(src="../../../assets/arrow-prev.svg")
          button.slider-arrows__item.slider-arrow(type="button" @click="changeSlide(1)", :disabled="scroll >= maxScroll - offset")
            img.slider-arrow__static(src="../../../assets/arrow-next-blue.svg")
            img.slider-arrow__hover(src="../../../assets/arrow-next.svg")
      .slider-container
        .slider-scrollable(ref="scrollable" @scroll="getScroll")
          .slider-track(ref="track")
            .slide(v-for="slide, index in content.items.media", :key="index" ref="slides")
              img(:src="slide.url" alt="slider image" ref="img")

</template>


<!--
               .               oooo
             .o8               `888
   .oooo.o .o888oo oooo    ooo  888   .ooooo.   .oooo.o
  d88(  "8   888    `88.  .8'   888  d88' `88b d88(  "8
  `"Y88b.    888     `88..8'    888  888ooo888 `"Y88b.
  o.  )88b   888 .    `888'     888  888    .o o.  )88b
  8""888P'   "888"     .8'     o888o `Y8bod8P' 8""888P'
                   .o..P'
                   `Y8P'
 -->


<style lang="stylus" scoped>

.widget
  position relative
  padding-top 60px
  padding-bottom 60px
  display flex
  justify-content center

  +mobile()
    padding-top 35px

  &__background
    z-index 1
    position absolute
    top 0
    left 0
    width 100%
    height 100%
    background-color #f9f9f9
    transform-origin top

  &__content
    z-index 2
    position relative
    width 100%
    display flex
    flex-direction column
    align-items center
    padding-top 80px
    padding-bottom 135px

    +tablet()
      padding-top 15px
      padding-bottom 90px

    +mobile()
      padding-bottom 48px

  &__text
    display flex
    flex-direction column
    width 554px
    max-width 46.5vw
    min-width 455px
    padding 60px 0

    +mobile()
      min-width 0
      max-width 100%
      padding 35px 24px

    .widget-title
      font-weight 500
      font-size 12px
      line-height 20px
      color blue

      & + *
        margin-top 24px

  &__slider
    width 100%
    display flex
    flex-direction column
    justify-content center

// ===========================================
// Slider
// ===========================================

.slider
  width 100%

.slider-arrows-container
  // max-width 1140px
  width 100%
  display flex
  justify-content flex-end
  margin-bottom 35px

.slider-arrows
  display flex
  justify-content space-between
  margin-right 16%
  margin-top -9px

  +tablet()
    margin-right 50px
  +mobile()
    margin-right 24px

  &__item
    position relative

.slider-arrow
  position relative
  display flex
  justify-content center
  align-items center
  padding 9px
  outline 0
  border 0
  background none
  transition transform .3s, opacity .3s

  &:disabled
    opacity .3
    pointer-events none

  img
    transition opacity .1s

  &:first-child:hover
    transform translateX(-5px)

  &:last-child:hover
    transform translateX(5px)

  &__hover
    position absolute
    top 0
    left 0
    right 0
    bottom 0
    margin auto
    opacity 0
    visibility hidden

  &:hover &__hover
    opacity 1
    visibility visible

.slider-container
  width 100%
  height 468px
  overflow hidden
  position: relative
  &:after
    content ''
    position: absolute
    right: 0
    top: 0
    width: 20px
    height: 100%
    background: #f9f9f9;
  +tablet()
    max-height: 300px
  +mobile()
    max-height: 200px

.slider-scrollable
  width 100%
  display flex
  overflow-y scroll
  +tablet()
    height: calc(100% + 20px);
    padding-bottom: 0

.slider-track
  display flex
  white-space nowrap
  +tablet()
    height: 300px
  +mobile()
    height: 200px
  .slide
    height: 500px
    width: 850px
    +tablet()
      height: 300px
      width: 500px
    +mobile()
      width: auto
      height: 200px

.slide
  display inline-flex
  height 100%

  &:first-child
    margin-left 66px

  &:last-child
    margin-right 66px

  & + &
    margin-left 60px

    +mobile()
      margin-left: 20px

  img
    height 100%

</style>



// WEBPACK FOOTER //
// slider.vue?7eb8bf98